import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useAuthConfig } from "@/lib/api";
import { postLocalInit } from "@/lib/api/core";
import { useAuthState } from "@/lib/auth-state";

/**
 * Single-user local installs sign in automatically via POST /api/local-init.
 *
 * The SPA tries whenever `singleUserMode` is on — the *server* owns the trust
 * boundary (loopback peer + optional LOBU_LOCAL_INIT_ALLOW_PROXY for private
 * Tailscale Serve). Over a public tunnel without that flag the call 403s and
 * we leave the normal unauthenticated UI alone.
 *
 * `/auth/login` has its own handshake UI; this hook covers every other route
 * so deep links don't 401 their data before a session exists.
 */
export function useLocalInstallAutoSignIn() {
	const { session, sessionResult, isReady } = useAuthState();
	const shouldCheckLocalInstall =
		typeof window !== "undefined" &&
		!window.location.pathname.startsWith("/auth/");
	const { data: authConfig } = useAuthConfig({
		enabled: shouldCheckLocalInstall,
	});
	const queryClient = useQueryClient();
	const attempted = useRef(false);

	useEffect(() => {
		if (!isReady || session || attempted.current) return;
		if (!authConfig?.singleUserMode) return;

		attempted.current = true;
		void (async () => {
			try {
				const res = await postLocalInit();
				if (res.ok) {
					// Refetch the session, then invalidate all queries so data that 401'd
					// before the session existed (e.g. the agents list) refetches and the
					// UI re-renders signed in — without a full page reload.
					await sessionResult.refetch();
					// Invalidation can reuse an in-flight first fetch. Cancel it so
					// an anonymous denial cannot win after the session is ready.
					await queryClient.cancelQueries();
					await queryClient.invalidateQueries();
				}
			} catch {
				// Leave the user on the normal unauthenticated UI; /auth/login can retry.
			}
		})();
	}, [isReady, session, authConfig, sessionResult, queryClient]);
}
